var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Select } from 'antd';
import services from 'services';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { codeColumn, monoColumn, NameText, showColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { Product as ProductResource } from 'constants/resources';
import { downloadCsv, todayStamp } from '../csv';
import ProductDrawer from './components/ProductDrawer';
import './style.css';
var textOf = function (value) { return (value == null ? '' : String(value)); };
var categoryName = function (product) { var _a; return textOf((_a = product.getProductCategory()) === null || _a === void 0 ? void 0 : _a.getName()); };
var unitName = function (product) { var _a; return textOf((_a = product.getUnitMeasurement()) === null || _a === void 0 ? void 0 : _a.getName()); };
var formatQuantity = function (value) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);
};
var Products = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var productService = services.productService, productCategoryService = services.productCategoryService, permissionService = services.permissionService;
    var canAdd = permissionService.hasPermissionToAdd(ProductResource);
    var canShow = permissionService.hasPermissionToShow(ProductResource);
    var _a = useState(1), pageNumber = _a[0], setPageNumber = _a[1];
    var _b = useState(''), searchValue = _b[0], setSearchValue = _b[1];
    var _c = useState('all'), categoryFilter = _c[0], setCategoryFilter = _c[1];
    var _d = useState('all'), stockFilter = _d[0], setStockFilter = _d[1];
    var _e = useState(false), exporting = _e[0], setExporting = _e[1];
    var _f = useState(false), drawerOpen = _f[0], setDrawerOpen = _f[1];
    var debounceRef = useRef(undefined);
    var _g = productService.useProducts(pageNumber), data = _g.data, isLoading = _g.isLoading;
    var categories = productCategoryService.useProductCategories().data;
    var products = (data === null || data === void 0 ? void 0 : data.products) || [];
    var meta = data === null || data === void 0 ? void 0 : data.meta;
    var page = Number((meta === null || meta === void 0 ? void 0 : meta.page) || pageNumber);
    var pages = Math.max(1, Number((meta === null || meta === void 0 ? void 0 : meta.pages) || 1));
    var total = Number((meta === null || meta === void 0 ? void 0 : meta.count) || products.length);
    var from = Number((meta === null || meta === void 0 ? void 0 : meta.from) || (products.length ? 1 : 0));
    var to = Number((meta === null || meta === void 0 ? void 0 : meta.to) || products.length);
    var categoryOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('allCategories') }
    ], (categories || []).map(function (category) { return ({
        value: category.getId(),
        label: category.getName()
    }); }), true); }, [categories, t]);
    var stockOptions = useMemo(function () { return [
        { value: 'all', label: t('stockStatusAll') },
        { value: 'in', label: t('stockStatusIn') },
        { value: 'out', label: t('stockStatusOut') }
    ]; }, [t]);
    var visible = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        return products.filter(function (product) {
            var _a;
            var quantity = product.getQuantity();
            if (categoryFilter !== 'all') {
                var id = ((_a = product.getProductCategory()) === null || _a === void 0 ? void 0 : _a.getId()) || product.getProductCategoryId();
                if (Number(id) !== categoryFilter)
                    return false;
            }
            if (stockFilter === 'in' && quantity <= 0)
                return false;
            if (stockFilter === 'out' && quantity > 0)
                return false;
            if (!query)
                return true;
            return [
                product.getId(),
                product.getName(),
                product.getBarcode(),
                product.getSystemCode(),
                categoryName(product)
            ]
                .join(' ')
                .toLowerCase()
                .includes(query);
        });
    }, [products, searchValue, categoryFilter, stockFilter]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () { return setSearchValue(value); }, 300);
    };
    var openProduct = useCallback(function (id) {
        if (!canShow)
            return;
        navigate("/products/".concat(id));
    }, [canShow, navigate]);
    var exportProducts = function () {
        if (exporting)
            return;
        if (!visible.length) {
            message.warning(t('exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [t('id'), t('name'), t('barcode'), t('systemCode'), t('quantity'), t('category')]
            ], visible.map(function (product) { return [
                String(product.getId()),
                textOf(product.getName()),
                textOf(product.getBarcode()),
                textOf(product.getSystemCode()),
                [formatQuantity(product.getQuantity()), unitName(product)].filter(Boolean).join(' '),
                categoryName(product)
            ]; }), true);
            downloadCsv("products-page-".concat(page, "-").concat(todayStamp(), ".csv"), rows);
        }
        catch (_a) {
            message.error(t('exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return __spreadArray([
        monoColumn({ key: 'id', title: t('id'), width: '72px', variant: 'id', value: function (product) { return product.getId(); } }),
        {
            key: 'name',
            title: t('name'),
            width: 'minmax(180px, 1.4fr)',
            render: function (product) {
                var stockClass = product.getQuantity() > 0 ? 'is-in' : 'is-out';
                return (_jsxs("span", { className: "prod-name", children: [_jsx("span", { className: "prod-name__dot ".concat(stockClass), "aria-hidden": true }), _jsx(NameText, { children: textOf(product.getName()) })] }));
            }
        },
        codeColumn({ key: 'code', title: t('systemCode'), width: 'minmax(120px, 1fr)', value: function (product) { return textOf(product.getSystemCode()); } }),
        {
            key: 'qty',
            title: t('quantity'),
            width: '140px',
            render: function (product) {
                var unit = unitName(product);
                var stockClass = product.getQuantity() > 0 ? 'is-in' : 'is-out';
                return (_jsxs("span", { className: "prod-qty ".concat(stockClass), children: [formatQuantity(product.getQuantity()), unit ? _jsx("span", { className: "prod-qty__unit", children: unit }) : null] }));
            }
        },
        { key: 'category', title: t('category'), width: 'minmax(140px, 1fr)', render: function (product) { return _jsx("span", { className: "prod-category", children: categoryName(product) || '—' }); } }
    ], (canShow ? [showColumn({
            title: t('action'),
            plain: true,
            label: t('common:buttons.show'),
            onShow: function (product) { return openProduct(product.getId()); }
        })] : []), true); }, [t, canShow, openProduct]);
    var filtering = Boolean(searchValue.trim()) || categoryFilter !== 'all' || stockFilter !== 'all';
    return (_jsxs(ListScreen, { className: "products-page", leading: (_jsxs("nav", { className: "products-page__crumb", "aria-label": t('sidebar:inventory'), children: [_jsx("span", { children: t('sidebar:inventory') }), _jsx("span", { "aria-hidden": true, children: "/" }), _jsx("span", { className: "products-page__crumb-current", children: t('sidebar:products') })] })), title: t('title'), count: t('countBadge', { count: total }), subtitle: t('subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('export'), busyLabel: t('exporting'), busy: exporting, onClick: exportProducts }), canAdd && _jsx(ListAddButton, { label: t('common:buttons.newProduct'), onClick: function () { return setDrawerOpen(true); } })] })), search: { placeholder: t('searchPlaceholder'), onChange: onSearchValueChange }, toolbarEnd: (_jsxs("div", { className: "products-page__filters", children: [_jsx(Select, { className: "cidx-select", value: categoryFilter, onChange: function (value) { return setCategoryFilter(value); }, options: categoryOptions, popupMatchSelectWidth: false, "aria-label": t('allCategories') }), _jsx(Select, { className: "cidx-select", value: stockFilter, onChange: function (value) { return setStockFilter(value); }, options: stockOptions, popupMatchSelectWidth: false, "aria-label": t('stockStatusAll') })] })), children: [_jsx(DataTable, { minWidth: "860px", loading: isLoading, rows: visible, rowKey: function (product) { return product.getId(); }, onRowClick: canShow ? function (product) { return openProduct(product.getId()); } : undefined, empty: _jsx(EmptyComponent, { description: t('empty') }), columns: columns, pager: listPager({
                    total: total,
                    page: page,
                    pages: pages,
                    meta: filtering ? t('matchesOnPage', { count: visible.length }) : t('showingRange', { from: from, to: to, total: total }),
                    previous: t('previous'),
                    next: t('next'),
                    center: pages > 1 ? t('pageOf', { page: page, pages: pages }) : undefined,
                    setPage: setPageNumber
                }) }), canAdd && (_jsx(ProductDrawer, { visiable: drawerOpen, onCloseDrawer: function () { return setDrawerOpen(false); } }))] }));
};
export default localeTranslator(Products, 'products');
